// These helpers are calling this template's own server-side routes
// so, they are not directly calling Marketplace API or Integration API.
// You can find these api endpoints from 'server/api/...' directory

import appSettings from '../config/settings';
import { types as sdkTypes, transit } from './sdkLoader';
import Decimal from 'decimal.js';

export const apiBaseUrl = marketplaceRootURL => {
  const isDev = process.env.NODE_ENV === 'development';
  if (isDev && process.env.REACT_APP_PROXY_API === 'true') {
    return '';
  }
  if (isDev && process.env.REACT_APP_API_BASE_URL) {
    return process.env.REACT_APP_API_BASE_URL.replace(/\/$/, '');
  }
  const port = process.env.REACT_APP_DEV_API_SERVER_PORT;
  const useDevApiServer = isDev && !!port;

  // In development, the dev API server is running in a different port
  if (useDevApiServer) {
    return `http://localhost:${port}`;
  }

  // Otherwise, use the given marketplaceRootURL parameter or the same domain and port as the frontend
  return marketplaceRootURL ? marketplaceRootURL.replace(/\/$/, '') : `${window.location.origin}`;
};

// Application type handlers for JS SDK.
//
// NOTE: keep in sync with `typeHandlers` in `server/api-util/sdk.js`
export const typeHandlers = [
  // Use Decimal type instead of SDK's BigDecimal.
  {
    type: sdkTypes.BigDecimal,
    customType: Decimal,
    writer: v => new sdkTypes.BigDecimal(v.toString()),
    reader: v => new Decimal(v.value),
  },
];

const serialize = data => {
  return transit.write(data, { typeHandlers, verbose: appSettings.sdk.transitVerbose });
};

const deserialize = str => {
  return transit.read(str, { typeHandlers });
};

const methods = {
  POST: 'POST',
  GET: 'GET',
  PUT: 'PUT',
  PATCH: 'PATCH',
  DELETE: 'DELETE',
};

// If server/api returns data from SDK, you should set Content-Type to 'application/transit+json'
const request = (path, options = {}) => {
  const url = `${apiBaseUrl()}${path}`;
  const { credentials, headers, body, ...rest } = options;

  // If headers are not set, we assume that the body should be serialized as transit format.
  const shouldSerializeBody =
    (!headers || headers['Content-Type'] === 'application/transit+json') && body;
  
  let bodyMaybe = {};
  if (shouldSerializeBody) {
    bodyMaybe = { body: serialize(body) };
  } else if (body) {
    bodyMaybe = { body: typeof body === 'string' ? body : JSON.stringify(body) };
  }

  const fetchOptions = {
    credentials: credentials || 'include',
    // Since server/api mostly talks to Marketplace API using SDK,
    // we default to 'application/transit+json' as content type (as SDK uses transit).
    headers: headers || { 'Content-Type': 'application/transit+json' },
    ...bodyMaybe,
    ...rest,
  };

  return window.fetch(url, fetchOptions).then(res => {
    const contentTypeHeader = res.headers.get('Content-Type');
    const contentType = contentTypeHeader ? contentTypeHeader.split(';')[0] : null;

    if (res.status >= 400) {
      return res.json().then(data => {
        let e = new Error();
        e = Object.assign(e, data);

        throw e;
      });
    }
    if (contentType === 'application/transit+json') {
      return res.text().then(deserialize);
    } else if (contentType === 'application/json') {
      return res.json();
    }
    return res.text();
  });
};

// Keep the previous parameter order for the post method.
// For now, only POST has own specific function, but you can create more or use request directly.
const post = (path, body, options = {}) => {
  const requestOptions = {
    ...options,
    method: methods.POST,
    body,
  };

  return request(path, requestOptions);
};

// Fetch transaction line items from the local API endpoint.
//
// See `server/api/transaction-line-items.js` to see what data should
// be sent in the body.
export const transactionLineItems = body => {
  return post('/api/transaction-line-items', body);
};

// Initiate a privileged transaction.
//
// With privileged transitions, the transactions need to be created
// from the backend. This endpoint enables sending the order data to
// the local backend, and passing that to the Marketplace API.
//
// See `server/api/initiate-privileged.js` to see what data should be
// sent in the body.
export const initiatePrivileged = body => {
  return post('/api/initiate-privileged', body);
};

// Transition a transaction with a privileged transition.
//
// This is similar to the `initiatePrivileged` above. It will use the
// backend for the transition. The backend endpoint will add the
// payment line items to the transition params.
//
// See `server/api/transition-privileged.js` to see what data should
// be sent in the body.
export const transitionPrivileged = body => {
  return post('/api/transition-privileged', body);
};

// Create user with identity provider (e.g. Facebook or Google)
//
// If loginWithIdp api call fails and user can't authenticate to Marketplace API with idp
// we will show option to create a new user with idp.
// For that user needs to confirm data fetched from the idp.
// After the confirmation, this endpoint is called to create a new user with confirmed data.
//
// See `server/api/auth/createUserWithIdp.js` to see what data should
// be sent in the body.
export const createUserWithIdp = body => {
  return post('/api/auth/create-user-with-idp', body);
};

// Check if user can be deleted and then delete the user. Endpoint logic
// must be modified to accommodate the transaction processes used in
// the marketplace.
export const deleteUserAccount = body => {
  return post('/api/delete-account', body);
};

// ============================================================================
// REZDY INTEGRATION
// ============================================================================

/**
 * Notify Rezdy about a listing publish or update
 * Rezdy will then pull the listing data from the RezdyConnect endpoint
 * 
 * @param {string} listingId - The Sharetribe listing UUID
 * @param {string} action - Either "published" or "updated"
 * 
 * @returns {Promise} - Response from Rezdy notification
 * 
 * @example
 * notifyRezdySync('listing-abc-123', 'published')
 */
export const notifyRezdySync = (listingId, action = 'published', tab = null) => {
  return post(
    '/api/rezdy-sync/notify',
    { listingId, action, tab },
    { headers: { 'Content-Type': 'application/json' } }
  );
};

/**
 * Upload an image to Cloudinary via the template's backend API.
 * The backend will generate the signature and perform the upload.
 * 
 * @param {string} file - Base64 encoded file string
 * @param {string} experience - Either "simple_screen" or "golden_screen"
 * 
 * @returns {Promise} - Resolves with { secure_url }
 */
export const uploadCloudinary = (file, experience) => {
  return post('/api/cloudinary-upload', { file, experience }, {
    headers: { 'Content-Type': 'application/json' },
  });
};

/**
 * Delete an image from Cloudinary dashboard via the template's backend API.
 * 
 * @param {string} publicId - The public ID of the Cloudinary image to destroy
 * 
 * @returns {Promise} - Resolves with { result }
 */
export const deleteCloudinary = publicId => {
  return post('/api/cloudinary-delete', { publicId }, {
    headers: { 'Content-Type': 'application/json' },
  });
};

/**
 * Create a direct upload URL on Mux.
 * 
 * @returns {Promise} - Resolves with Mux upload details { id, url, status, ... }
 */
export const createMuxUpload = () => {
  return post('/api/mux-upload/create', {}, {
    headers: { 'Content-Type': 'application/json' },
  });
};

/**
 * Get status of a Mux upload.
 * 
 * @param {string} uploadId - Mux upload ID
 * 
 * @returns {Promise} - Resolves with upload status or asset playback details
 */
export const getMuxUploadStatus = uploadId => {
  return request(`/api/mux-upload/status?uploadId=${uploadId}`, {
    method: 'GET',
    headers: { 'Accept': 'application/json' },
  });
};


/**
 * Release a Xendit payout for an accepted booking.
 *
 * The server derives payout amount and provider bank details from Sharetribe,
 * so the browser only sends the transaction id.
 */
export const releaseXenditPayout = body => {
  return post('/api/xendit/release-payout', body, {
    headers: { 'Content-Type': 'application/json' },
  });
};

// Update transaction metadata securely via template's backend endpoint.
export const updateTransactionMetadata = body => {
  return post('/api/update-metadata', body, {
    headers: { 'Content-Type': 'application/json' },
  });
};

// Fetch Google Calendar availability for a given date and timezone.
export const fetchGoogleCalendarAvailability = params => {
  const { date, timezone, providerId, providerIds, listingId, startTime, endTime } = params;
  let url = `/api/google-calendar/availability?date=${encodeURIComponent(date)}&timezone=${encodeURIComponent(timezone)}`;
  if (listingId) {
    url += `&listingId=${encodeURIComponent(listingId)}`;
  }
  if (providerId) {
    url += `&providerId=${encodeURIComponent(providerId)}`;
  }
  if (providerIds) {
    url += `&providerIds=${encodeURIComponent(providerIds)}`;
  }
  if (startTime) {
    url += `&startTime=${encodeURIComponent(startTime)}`;
  }
  if (endTime) {
    url += `&endTime=${encodeURIComponent(endTime)}`;
  }
  return request(url, {
    method: 'GET',
  });
};

// Create a Google Calendar meeting/event.
export const createGoogleCalendarEvent = body => {
  return post('/api/google-calendar/create-event', body, {
    headers: { 'Content-Type': 'application/json' },
  });
};

// Capture a pre-authorized Stripe payment intent
export const captureStripePayment = body => {
  return post('/api/stripe/capture-intent', body, {
    headers: { 'Content-Type': 'application/json' },
  });
};

// Create a Stripe payment intent
export const createStripePaymentIntent = body => {
  return post('/api/stripe/create-intent', body, {
    headers: { 'Content-Type': 'application/json' },
  });
};

// Confirm a Stripe payment intent
export const confirmStripePaymentIntent = body => {
  return post('/api/stripe/confirm-intent', body, {
    headers: { 'Content-Type': 'application/json' },
  });
};

// Invite a Service Provider
export const inviteProvider = body => {
  return post('/api/invite-provider', body, {
    headers: { 'Content-Type': 'application/json' },
  });
};

// Verify invitation token
export const verifyInviteToken = body => {
  return post('/api/verify-invite', body, {
    headers: { 'Content-Type': 'application/json' },
  });
};

// Get meetings from MongoDB
export const getMeetings = () => {
  return request('/api/meetings', {
    method: methods.GET,
    headers: { 'Content-Type': 'application/json' },
  });
};

// Update meeting status in MongoDB
export const updateMeetingStatus = body => {
  return post('/api/update-meeting-status', body, {
    headers: { 'Content-Type': 'application/json' },
  });
};

// Schedule phone call and save details in MongoDB
export const schedulePhoneCall = body => {
  return post('/api/schedule-phone-call', body, {
    headers: { 'Content-Type': 'application/json' },
  });
};

// Send a search guidance enquiry to the marketplace admin.
export const submitSearchGuidanceContact = body => {
  return post('/api/search-guidance-contact', body, {
    headers: { 'Content-Type': 'application/json' },
  });
};
// Get Rezdy bookings from MongoDB (Admin only)
export const getRezdyBookings = () => {
  return request('/api/rezdy-bookings', {
    method: methods.GET,
    headers: { 'Content-Type': 'application/json' },
  });
};


// Send a private access request to the marketplace admin.
export const submitPrivateAccessRequest = body => {
  return post('/api/private-access-request', body, {
    headers: { 'Content-Type': 'application/json' },
  });
};



// Check if a user with the specified email address exists.
export const checkUserExists = email => {
  return post('/api/check-user', { email }, {
    headers: { 'Content-Type': 'application/json' },
  });
};


// Check whether the selected experience date has remaining booking capacity.
export const getBookingDateAvailability = date => {
  return post('/api/booking-date-availability', { date }, {
    headers: { 'Content-Type': 'application/json' },
  });
};

// Validate promotional discount code
export const validateDiscountCodeApi = (code, email = null, userId = null) => {
  return post(
    '/api/discount/validate',
    {
      code,
      ...(email ? { email } : {}),
      ...(userId ? { userId } : {}),
    },
    {
      headers: { 'Content-Type': 'application/json' },
    }
  );
};

// Fallback stub for legacy banquet booking
export const bookBanquetOption = () => Promise.resolve({});

